"use client";

import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import Icon from "@/components/library/Icon";
import {VoiceProvider} from "@/enums/agencies";
import {useOrg} from "@/hooks/useOrg";
import {VoiceOption} from "@/types/agencies";
import {
  buildExploreCatalog,
  buildRecommendedFeaturedList,
  omitUnavailableSelectedVoice,
  promoteSelectedIntoFeatured,
  resolveSelectedVoiceOption,
} from "@/utils/scripts/compactVoiceList";
import VoiceCardGrid from "./VoiceCardGrid";
import VoiceExploreToolbar from "./VoiceExploreToolbar";
import VoiceLanguageCatalog from "./VoiceLanguageCatalog";
import {useVoiceCatalogFetch} from "./useVoiceCatalogFetch";
import {
  countActiveVoiceFilters,
  DEFAULT_VOICE_FILTERS,
  matchesExploreSearch,
  matchesVoiceFilters,
  VoiceFilters,
} from "./VoiceSelectorFilters";
import VoiceCatalogStatus from "./VoiceCatalogStatus";

interface VoiceSelectorProps {
  selectedVoiceId: string | undefined;
  onVoiceSelect: (voice: VoiceOption) => void;
  /**
   * Fired when the catalog resolves (or clears) the selected voice.
   * Does not imply the user clicked a card — use `onVoiceSelect` for that.
   */
  onResolvedVoiceChange?: (voice: VoiceOption | null) => void;
  orgId?: string;
  searchQuery?: string;
  filters?: VoiceFilters;
  /** When true, shows the compact featured row with an "Explore more voices" toggle */
  compact?: boolean;
  voiceProvider?: VoiceProvider | string | null;
  /** When true, do not pin a fallback card for a voice confirmed missing from the org. */
  voiceUnavailable?: boolean;
}

export default function VoiceSelector({
  selectedVoiceId,
  onVoiceSelect,
  onResolvedVoiceChange,
  orgId,
  searchQuery = "",
  filters = DEFAULT_VOICE_FILTERS,
  compact = false,
  voiceProvider,
  voiceUnavailable = false,
}: Readonly<VoiceSelectorProps>) {
  const {t} = useTranslation("inbound");
  const {t: ct} = useTranslation("common");
  const {t: st} = useTranslation("settings");
  const {agentProfile} = useOrg();
  const {voices, isLoadingVoices, voiceLoadFailed, fetchVoices} =
    useVoiceCatalogFetch(searchQuery);

  const [changeMarker, setChangeMarker] = useState("");
  const [showAllVoices, setShowAllVoices] = useState(!compact);
  const [exploreSearch, setExploreSearch] = useState("");
  const [exploreFilters, setExploreFilters] = useState<VoiceFilters>(DEFAULT_VOICE_FILTERS);
  const selectedVoiceRef = useRef<VoiceOption | null>(null);

  // Omit a confirmed-missing selection so it cannot be clicked again from a
  // stale catalog card.
  const filteredVoices = useMemo(
    () =>
      omitUnavailableSelectedVoice(
        voices.filter((voice) => matchesVoiceFilters(voice, filters)),
        selectedVoiceId,
        voiceUnavailable
      ),
    [voices, filters, selectedVoiceId, voiceUnavailable]
  );

  const selectedVoiceFallbackLabel = t("pages.agent.selectedVoiceFallback");
  const selectedVoice = useMemo(
    () =>
      resolveSelectedVoiceOption(selectedVoiceId, voices, {
        voiceProvider,
        previouslyResolved: selectedVoiceRef.current,
        fallbackDisplayName: selectedVoiceFallbackLabel,
        voiceUnavailable,
      }),
    [selectedVoiceId, voices, voiceProvider, selectedVoiceFallbackLabel, voiceUnavailable]
  );

  useEffect(() => {
    if (selectedVoice) {
      selectedVoiceRef.current = selectedVoice;
    } else if (!selectedVoiceId) {
      selectedVoiceRef.current = null;
    }
    onResolvedVoiceChange?.(selectedVoice);
  }, [selectedVoice, selectedVoiceId, onResolvedVoiceChange]);

  const catalogForPicker = useMemo(() => {
    if (selectedVoice && !filteredVoices.some((v) => v.id === selectedVoice.id)) {
      return [selectedVoice, ...filteredVoices];
    }
    return filteredVoices;
  }, [filteredVoices, selectedVoice]);

  const featuredVoices = useMemo(
    () => buildRecommendedFeaturedList(catalogForPicker),
    [catalogForPicker]
  );

  const featuredVoiceIds = useMemo(
    () => new Set(featuredVoices.map((voice) => voice.id)),
    [featuredVoices]
  );

  // Org-created custom voices are the org's own assets — surface them as
  // their own row right under the featured voices instead of burying them
  // behind "Explore more voices". An org voice that is also Gail Recommended
  // is not repeated here.
  const orgVoices = useMemo(
    () =>
      compact
        ? catalogForPicker.filter(
            (voice) => voice.isOrganizationVoice && !featuredVoiceIds.has(voice.id)
          )
        : [],
    [compact, catalogForPicker, featuredVoiceIds]
  );

  // Collapsed view: the current selection must stay visible, so when it is
  // neither recommended nor an org voice (which has its own row) it is
  // prepended to the featured row. Recommended cards are not dropped.
  // Expanding restores the plain recommended row and the selection is in
  // the catalog below.
  const collapsedFeaturedVoices = useMemo(() => {
    if (!selectedVoice || orgVoices.some((voice) => voice.id === selectedVoice.id)) {
      return featuredVoices;
    }
    return promoteSelectedIntoFeatured(featuredVoices, selectedVoice);
  }, [featuredVoices, orgVoices, selectedVoice]);

  const exploreVoices = useMemo(
    () =>
      buildExploreCatalog(
        catalogForPicker,
        featuredVoices,
        orgVoices,
        agentProfile.languages,
        selectedVoiceId
      ),
    [catalogForPicker, featuredVoices, orgVoices, agentProfile.languages, selectedVoiceId]
  );

  const remainingVoiceCount = exploreVoices.length;

  const catalogVoices = useMemo(() => {
    return exploreVoices
      .filter((voice) => matchesVoiceFilters(voice, exploreFilters))
      .filter((voice) => matchesExploreSearch(voice, exploreSearch));
  }, [exploreVoices, exploreFilters, exploreSearch]);

  const collapseCatalog = () => {
    setShowAllVoices(false);
    setExploreSearch("");
    setExploreFilters(DEFAULT_VOICE_FILTERS);
  };

  const gridProps = {
    selectedVoiceId,
    onVoiceSelect,
    changeMarker,
    setPlaying: setChangeMarker,
    translateCommon: ct,
    translateSettings: st,
    orgId,
  };

  if (isLoadingVoices || voiceLoadFailed || catalogForPicker.length === 0) {
    return (
      <VoiceCatalogStatus
        isLoading={isLoadingVoices}
        failed={voiceLoadFailed}
        isEmpty={catalogForPicker.length === 0}
        onRetry={() => fetchVoices(searchQuery)}
      />
    );
  }

  const orgVoicesRow =
    orgVoices.length > 0 ? (
      <div className="space-y-2">
        {/* Same size/weight as the "Gail's Recommended Voices" header above. */}
        <p className="text-sm font-semibold text-foreground">{t("pages.agent.orgVoicesTitle")}</p>
        <VoiceCardGrid voices={orgVoices} {...gridProps} />
      </div>
    ) : null;

  if (compact && !showAllVoices) {
    return (
      <div className="space-y-3">
        <VoiceCardGrid voices={collapsedFeaturedVoices} {...gridProps} />
        {orgVoicesRow}
        {remainingVoiceCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAllVoices(true)}
            aria-expanded={false}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors cursor-pointer"
          >
            <span>{t("pages.agent.exploreMoreVoices")}</span>
            <Icon icon="chevron-down" className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }

  const hasExploreQuery = Boolean(exploreSearch) || countActiveVoiceFilters(exploreFilters) > 0;

  return (
    <div className="space-y-4">
      <VoiceCardGrid voices={featuredVoices} {...gridProps} />
      {orgVoicesRow}
      {compact && (
        <button
          type="button"
          onClick={collapseCatalog}
          aria-expanded={true}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors cursor-pointer"
        >
          <span>{t("pages.agent.showFewerVoices")}</span>
          <Icon icon="chevron-up" className="w-3.5 h-3.5" />
        </button>
      )}
      <VoiceExploreToolbar
        search={exploreSearch}
        onSearchChange={setExploreSearch}
        filters={exploreFilters}
        onFiltersChange={setExploreFilters}
        catalog={exploreVoices}
      />

      {catalogVoices.length === 0 && hasExploreQuery ? (
        <div className="bg-muted/30 p-6 border border-border/70 rounded-xl text-muted-foreground text-sm text-center space-y-2">
          <p>{t("pages.agent.noVoicesMatchFilter")}</p>
          <button
            type="button"
            onClick={() => {
              setExploreSearch("");
              setExploreFilters(DEFAULT_VOICE_FILTERS);
            }}
            className="text-sm font-medium text-primary hover:underline cursor-pointer"
          >
            {t("pages.agent.filters.clearFilters")}
          </button>
        </div>
      ) : (
        catalogVoices.length > 0 && (
          <VoiceLanguageCatalog
            voices={catalogVoices}
            orgLanguages={agentProfile.languages}
            translateInbound={t}
            {...gridProps}
          />
        )
      )}
    </div>
  );
}
